import { useState, type FormEvent } from 'react'
import type { Role } from '@shared/types'
import { COIN_NAMES, formatCopper, splitCoins, type CoinType } from '@shared/rules/coins'
import { ApiError, api } from '@/lib/api'
import { useAsync } from '@/lib/useAsync'
import {
  Badge, Button, EmptyState, ErrorNote, Field, Input, NumberField, Panel, SectionHeading, Select,
} from '@/components/ui'
import { cn } from '@/lib/cn'

const COINS: CoinType[] = ['pp', 'gp', 'ep', 'sp', 'cp']

export function FundsTab({ campaignId, myRole }: { campaignId: string; myRole: Role }) {
  const { data, error, loading, reload } = useAsync(() => api.getLedger(campaignId), [campaignId])
  const [actionError, setActionError] = useState<string | null>(null)

  const canWrite = myRole !== 'guest'
  const isDm = myRole === 'dm'
  const balance = data?.balanceCp ?? 0
  const purse = splitCoins(balance)

  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
        <Panel className="p-5">
          <SectionHeading>Party purse</SectionHeading>
          <p className={cn('font-mono text-3xl tabular-nums', balance < 0 ? 'text-blood-500' : 'text-gold-400')}>
            {formatCopper(balance)}
          </p>
          <div className="mt-4 grid grid-cols-3 gap-2 border-t border-ink-700/60 pt-4 text-center">
            {(['gp', 'sp', 'cp'] as const).map((coin) => (
              <div key={coin}>
                <p className="font-mono text-lg tabular-nums text-ink-100">{purse[coin]}</p>
                <p className="text-[10px] uppercase tracking-wider text-ink-500">{COIN_NAMES[coin]}</p>
              </div>
            ))}
          </div>
          {balance < 0 && (
            <p className="mt-3 text-xs text-blood-500">
              The party is in the red. Someone has recorded more spending than income.
            </p>
          )}
        </Panel>

        {canWrite ? (
          <AddEntry campaignId={campaignId} onAdded={reload} onError={setActionError} />
        ) : (
          <Panel className="grid place-items-center p-5 text-sm text-ink-500">
            Guests can see the balance but cannot change it.
          </Panel>
        )}
      </div>

      {(error || actionError) && <ErrorNote>{actionError ?? error}</ErrorNote>}

      <section>
        <SectionHeading>Every coin in and out</SectionHeading>
        {loading ? (
          <Panel className="p-6 text-center text-sm text-ink-500">Loading…</Panel>
        ) : !data?.entries.length ? (
          <EmptyState
            title="Nothing recorded yet"
            description="Add the party's starting gold, then log loot and spending as it happens. Everyone can see who recorded what."
          />
        ) : (
          <Panel className="divide-y divide-ink-700/60">
            {data.entries.map((entry) => (
              <div key={entry.id} className="flex items-center gap-3 px-4 py-2.5">
                <span
                  className={cn(
                    'w-28 shrink-0 font-mono text-sm tabular-nums',
                    entry.deltaCp < 0 ? 'text-blood-500' : 'text-moss-500',
                  )}
                >
                  {formatCopper(entry.deltaCp, { sign: true })}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-ink-200">{entry.reason}</span>
                <span className="hidden shrink-0 text-xs text-ink-500 sm:inline">
                  {entry.actorName ?? 'someone'} · {new Date(entry.createdAt).toLocaleDateString()}
                </span>
                {isDm && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0"
                    aria-label={`Remove entry: ${entry.reason}`}
                    onClick={async () => {
                      setActionError(null)
                      try {
                        await api.deleteLedgerEntry(campaignId, entry.id)
                        reload()
                      } catch (err) {
                        setActionError(err instanceof ApiError ? err.message : 'Could not remove that')
                      }
                    }}
                  >
                    ✕
                  </Button>
                )}
              </div>
            ))}
          </Panel>
        )}
      </section>
    </div>
  )
}

function AddEntry({
  campaignId, onAdded, onError,
}: {
  campaignId: string
  onAdded: () => void
  onError: (message: string | null) => void
}) {
  const [direction, setDirection] = useState<'gain' | 'spend'>('gain')
  const [amount, setAmount] = useState(0)
  const [coin, setCoin] = useState<CoinType>('gp')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    onError(null)
    try {
      await api.addLedgerEntry(campaignId, { amount, coin, direction, reason: reason.trim() })
      setAmount(0)
      setReason('')
      onAdded()
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not record that')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Panel className="p-5">
      <SectionHeading>Record a change</SectionHeading>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-1 rounded-xl bg-ink-900/70 p-1">
          {(['gain', 'spend'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setDirection(value)}
              className={cn(
                'rounded-lg py-1.5 text-[13px] font-medium transition-colors',
                direction === value
                  ? value === 'gain' ? 'bg-moss-900 text-moss-500' : 'bg-blood-900 text-blood-500'
                  : 'text-ink-400 hover:text-ink-200',
              )}
            >
              {value === 'gain' ? 'Party gained' : 'Party spent'}
            </button>
          ))}
        </div>

        <div className="flex gap-3">
          <Field label="Amount">
            {(id) => (
              <NumberField
                id={id}
                min={0}
                max={1000000}
                fallback={0}
                value={amount}
                onChange={setAmount}
                className="w-28"
              />
            )}
          </Field>
          <Field label="Coin">
            {(id) => (
              <Select id={id} value={coin} onChange={(e) => setCoin(e.target.value as CoinType)} className="w-32">
                {COINS.map((value) => (
                  <option key={value} value={value}>{value} — {COIN_NAMES[value]}</option>
                ))}
              </Select>
            )}
          </Field>
        </div>

        <Field label="What for?">
          {(id) => (
            <Input
              id={id}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={200}
              placeholder={direction === 'gain' ? 'Sold the griffon feather' : 'Rooms at the Hollow Tree'}
            />
          )}
        </Field>

        <div className="flex items-center justify-between gap-3">
          <Badge tone={direction === 'gain' ? 'moss' : 'blood'}>
            {direction === 'gain' ? '+' : '−'}{amount || 0} {coin}
          </Badge>
          <Button type="submit" variant="primary" busy={busy} disabled={!amount || !reason.trim()}>
            Record it
          </Button>
        </div>
      </form>
    </Panel>
  )
}
